import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Radio, Plus, Send, Power } from "lucide-react";

export default function LiveBlogAdminPage() {
  const [blogs, setBlogs] = useState([]);
  const [active, setActive] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newSub, setNewSub] = useState("");
  const [entry, setEntry] = useState({ content: "", tag: "update" });

  const load = () => {
    api.get("/admin/live-blog").then((r) => {
      setBlogs(r.data);
      setActive(r.data.find((b) => b.active) || null);
    });
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newTitle.trim()) { toast.error("शीर्षक दें"); return; }
    try {
      await api.post("/admin/live-blog", { title: newTitle, subtitle: newSub, active: true });
      setNewTitle(""); setNewSub(""); toast.success("Live blog शुरू");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const addEntry = async () => {
    if (!active || !entry.content.trim()) { toast.error("संदेश लिखें"); return; }
    try {
      await api.post(`/admin/live-blog/${active.id}/entry`, entry);
      setEntry({ content: "", tag: "update" }); toast.success("अपडेट पोस्ट हो गया");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const toggle = async (id, val) => {
    try { await api.patch(`/admin/live-blog/${id}`, { active: val }); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="admin-live-blog-page">
      <div className="mb-6"><h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A] flex items-center gap-3"><Radio className="w-6 h-6 text-red-500"/>लाइव ब्लॉग</h1>
        <p className="text-sm text-slate-500 mt-1">ब्रेकिंग इवेंट के लिए लाइव अपडेट्स भेजें</p></div>

      {active ? (
        <div className="bg-white border-2 border-red-300 rounded-lg p-5 mb-6" data-testid="active-lb">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            <span className="uppercase tracking-widest text-[10px] font-bold text-red-700">LIVE</span>
            <span className="font-bold text-[#0F172A]">{active.title}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
            <select value={entry.tag} onChange={(e) => setEntry({ ...entry, tag: e.target.value })}
              className="h-10 px-3 rounded-md border border-slate-200" data-testid="entry-tag">
              <option value="update">Update</option><option value="alert">Alert</option><option value="quote">Quote</option>
            </select>
            <Textarea rows={2} className="md:col-span-3" placeholder="लाइव अपडेट लिखें…" value={entry.content}
              onChange={(e) => setEntry({ ...entry, content: e.target.value })} data-testid="entry-content"/>
          </div>
          <div className="flex justify-between items-center">
            <Button onClick={() => toggle(active.id, false)} variant="outline" size="sm" data-testid="stop-lb"><Power className="w-3.5 h-3.5 mr-1"/>बंद करें</Button>
            <Button onClick={addEntry} className="bg-red-600 hover:bg-red-700" data-testid="entry-send"><Send className="w-4 h-4 mr-2"/>अपडेट भेजें</Button>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 space-y-2 max-h-64 overflow-y-auto">
            {(active.entries || []).slice().reverse().map((e) => (
              <div key={e.eid} className="text-xs border-l-2 border-slate-200 pl-2">
                <span className={`uppercase text-[9px] font-bold ${e.tag === "alert" ? "text-red-600" : e.tag === "quote" ? "text-amber-600" : "text-blue-600"}`}>{e.tag}</span>
                <span className="text-slate-400 ml-2">{new Date(e.at).toLocaleString("hi-IN")}</span>
                <div className="text-slate-700 mt-0.5">{e.content}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
          <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><Plus className="w-4 h-4"/>नया लाइव ब्लॉग शुरू करें</h3>
          <Input placeholder="शीर्षक — जैसे: चुनाव नतीजे लाइव" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="mb-2" data-testid="lb-new-title"/>
          <Input placeholder="उपशीर्षक (वैकल्पिक)" value={newSub} onChange={(e) => setNewSub(e.target.value)} className="mb-3" data-testid="lb-new-sub"/>
          <Button onClick={create} className="bg-red-600 hover:bg-red-700 text-white" data-testid="lb-create"><Radio className="w-4 h-4 mr-2"/>लाइव शुरू करें</Button>
        </div>
      )}

      <div>
        <h3 className="font-bold text-[#0F172A] mb-2">पुराने लाइव ब्लॉग</h3>
        <div className="space-y-2">
          {blogs.filter((b) => !b.active).map((b) => (
            <div key={b.id} className="bg-white border border-slate-100 rounded-lg p-3 flex items-center justify-between">
              <div><div className="font-medium text-[#0F172A]">{b.title}</div><div className="text-[10px] text-slate-400">{new Date(b.created_at).toLocaleString("hi-IN")} · {b.entries?.length || 0} entries</div></div>
              <Button size="sm" variant="outline" onClick={() => toggle(b.id, true)}>पुनः शुरू</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
